import { useState, useRef } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { Button } from '@bancobarigui/barihaus';
import { PlusOutlined } from '@ant-design/icons';
import { Section, Group } from '../types';
import { QuestionItem } from '../QuestionItem';

interface SectionContentProps {
  section: Section;
  groupId: string;
  onAddQuestion: (insertIndex?: number) => void;
  onEditQuestion: (questionId: string, updates: any) => void;
  onRemoveQuestion: (questionId: string) => void;
  onDuplicateQuestion: (questionId: string) => void;
  allGroups: Group[];
  isDragging: boolean;
  draggedType?: string | null;
}

export function SectionContent({
  section,
  groupId,
  onAddQuestion,
  onEditQuestion,
  onRemoveQuestion,
  onDuplicateQuestion,
  allGroups,
  isDragging,
  draggedType,
}: SectionContentProps) {
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const questionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const scrollIntervalRef = useRef<number | null>(null);

  const handleAutoScroll = (mouseY: number) => {
    const scrollThreshold = 100;
    const scrollSpeed = 10;

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    if (mouseY < scrollThreshold) {
      scrollIntervalRef.current = window.setInterval(() => {
        window.scrollBy(0, -scrollSpeed);
      }, 16);
    }
    else if (mouseY > window.innerHeight - scrollThreshold) {
      scrollIntervalRef.current = window.setInterval(() => {
        window.scrollBy(0, scrollSpeed);
      }, 16);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedType !== 'new-question') {
      setDropIndex(null);
      return;
    }

    handleAutoScroll(e.clientY);

    if (section.questions.length === 0) {
      setDropIndex(0);
      return;
    }

    if (!containerRef.current) return;

    const mouseY = e.clientY;
    let targetIndex = section.questions.length;

    for (let i = 0; i < section.questions.length; i++) {
      const questionEl = questionRefs.current.get(i);
      if (questionEl) {
        const rect = questionEl.getBoundingClientRect();
        const middleY = rect.top + rect.height / 2;

        if (mouseY < middleY) {
          targetIndex = i;
          break;
        }
      }
    }

    setDropIndex(targetIndex);
  };

  const handleDragLeave = () => {
    setDropIndex(null);
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const controlType = e.dataTransfer.getData('text/plain');

    if (controlType === 'new-question' && dropIndex !== null) {
      onAddQuestion(dropIndex);
    }

    setDropIndex(null);

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  return (
    <>
      <Droppable
        droppableId={`group-${groupId}-section-${section.id}`}
        type="question"
        isDropDisabled={isDragging}
      >
        {(provided) => (
          <div
            ref={(el) => {
              provided.innerRef(el);
              if (el && containerRef.current !== el) {
                (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
              }
            }}
            {...provided.droppableProps}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              minHeight: section.questions.length === 0 ? '80px' : 'auto',
            }}
          >
            {section.questions.length === 0 && dropIndex === 0 && (
              <div
                className={`mt-2 mb-2 p-4 border-2 border-dashed rounded-lg transition-all ${
                  'border-blue-400 bg-blue-50'
                }`}
              >
                <p className="text-center text-sm mb-0 text-blue-500">
                  Solte aqui para adicionar primeira pergunta
                </p>
              </div>
            )}
            {section.questions.map((question, questionIndex) => (
              <div key={question.id}>
                {dropIndex === questionIndex && (
                  <div
                    className={`mt-2 mb-2 p-4 border-2 border-dashed rounded-lg transition-all ${
                      'border-blue-400 bg-blue-50'
                    }`}
                  >
                    <p className="text-center text-sm mb-0 text-blue-500">
                      Solte aqui para adicionar pergunta
                    </p>
                  </div>
                )}
                <div ref={(el) => el && questionRefs.current.set(questionIndex, el)}>
                  <QuestionItem
                    question={question}
                    index={questionIndex}
                    onEdit={(questionId, updates) => onEditQuestion(questionId, updates)}
                    onRemove={(questionId) => onRemoveQuestion(questionId)}
                    onDuplicate={(questionId) => onDuplicateQuestion(questionId)}
                    allGroups={allGroups}
                  />
                </div>
              </div>
            ))}
            {dropIndex === section.questions.length && section.questions.length > 0 && (
              <div
                className={`mt-2 mb-2 p-4 border-2 border-dashed rounded-lg transition-all ${
                  'border-blue-400 bg-blue-50'
                }`}
              >
                <p className="text-center text-sm mb-0 text-blue-500">
                  Solte aqui para adicionar pergunta
                </p>
              </div>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      <Button
        type="link"
        size="small"
        icon={<PlusOutlined />}
        onClick={() => onAddQuestion()}
        className="text-blue-600 font-medium p-0 mt-3"
      >
        Adicionar Pergunta
      </Button>
    </>
  );
}
